import React from 'react';
import { ILocation } from '../../types/resources/Location';
import { useTranslation } from 'react-i18next';
import DateUtils from '../../utils/DateUtils';
import styled from 'styled-components';
import LocationUtils from '../../utils/LocationUtils';

const CardWrapper = styled.div.attrs({
    className: 'cr-shadow cr-text-primary cr-rounded-block cr-w-100 cr-relative'
})`
    align-items: stretch;
    background-color: var(--cr-grey-light-color);
    display: grid;
    grid-template-columns: 1fr 2fr;

    &:not(:last-of-type):after {
        background: var(--cr-alert-color);
        content: '';
        position: absolute;
        left: 50%;
        top: 100%;
        width: 1px;
        height: var(--cr-spacing-xxl);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

type TImageProps = {
    $imageUrl?: string;
};

const Image = styled.div<TImageProps>`
    background-image: ${(props) => props.$imageUrl ? `linear-gradient(to left, #F7F7F7FF 0%, #F7F7F700 33.33%), url(${props.$imageUrl})` : 'none'};
    background-position: center;
    background-size: cover;
    border-top-left-radius: var(--cr-block-rounding);
    border-bottom-left-radius: var(--cr-block-rounding);

    @media (max-width: 768px) {
        aspect-ratio: 16/9;
        background-image: ${(props) => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
        border-top-right-radius: var(--cr-block-rounding);
        border-bottom-right-radius: var(--cr-block-rounding);

        mask-image: linear-gradient(black, black), var(--cr-mask-outer);
        mask-position: top, bottom;
        mask-repeat: no-repeat;
        mask-size: 100% calc(100% - 1rem), 100% 1rem;
    }
`;

const Content = styled.div.attrs({
    className: "cr-p-xl cr-flex cr-flex-col cr-gap-sm cr-text-secondary",
})`
    background: linear-gradient(to right,
      #F7F7F77E 0%,
      var(--cr-grey-light-color) 24px
    );
    position: relative;
    z-index: 0;

    &:before {
        background: #F7F7F7;
        content: '';
        height: 100%;
        left: -1px;
        position: absolute;
        top: 0;
        width: 2px;
        z-index: 1;
    }

    @media (max-width: 768px) {
        && {
            padding: var(--cr-spacing-lg);
        }
    }
`;

const ContentBody = styled.div`
    @media (max-width: 768px) {
        && {
            flex-direction: column;
        }
    }
`

const Table = styled.div`
    display: grid;
    grid-template-columns: repeat(2, auto);
`

const TableRow = styled.div`
    display: contents;

    @media (max-width: 768px) {
        display: inline-flex;
        gap: var(--cr-spacing-sm);
    }
`

const TableHeading = styled.span.attrs({
    className: 'cr-bold',
})`
`

const TableValue = styled.span.attrs({
    className: 'cr-text-icon',
})`
`

const Address = styled.div.attrs({
    className: 'cr-flex cr-flex-col cr-gap-lg'
})`
`

type TProps = {
    location: ILocation;
};

export default function LocationListItem({ location }: TProps) {
    const { t } = useTranslation();

    const date = location.startDateTime ? DateUtils.getHumanDate(location.startDateTime) : null;
    const time = location.startDateTime ? DateUtils.getHumanTime(location.startDateTime) : null;

    return (
        <CardWrapper>
            <Image $imageUrl={location.imageUrl} />
            <Content className={'cr-text-primary'}>
                <div>
                    <div>{location.typeDescription}</div>
                    <div className="cr-bold cr-text-alert cr-text-lg cr-pb">
                        {location.name}
                    </div>
                </div>
                <ContentBody className={'cr-flex cr-gap-lg cr-justify-between'}>
                    <div>
                        <Table className={'cr-column-gap-sm'}>
                            {date &&
                                <TableRow>
                                    <TableHeading>{t('date')}</TableHeading>
                                    <TableValue>{date}</TableValue>
                                </TableRow>
                            }
                            {time &&
                                <TableRow>
                                    <TableHeading>{t('time')}</TableHeading>
                                    <TableValue>{time}</TableValue>
                                </TableRow>
                            }
                        </Table>
                    </div>
                    <Address>
                        {location.address &&
                            <div>{location.address}</div>
                        }
                        {location.website &&
                            <a href={location.website} target="_blank" rel="noreferrer">{location.website}</a>
                        }
                        {location.address &&
                            <a
                                className="cr-pill cr-text-center"
                                href={LocationUtils.generateRouteUrl(location.address)} target="_blank" rel="noreferrer"
                            >
                                {t('plan-route')}
                            </a>
                        }
                    </Address>
                </ContentBody>
            </Content>
        </CardWrapper>
    );
};
